import axios from 'axios';

import { cvUploadFilename } from '@/utils/fileUpload';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TokenData = {
  success: boolean;
  valid: boolean;
  expired: boolean;
  params?: {
    branding?: string;
    recruiter_fullname?: string;
    recruiter?: {
      first_name?: string;
      last_name?: string;
      company_name?: string;
      job_title?: string;
    };
    user?: {
      local?: string;
      email?: string;
      first_name?: string;
      last_name?: string;
      job_title?: string;
      invited_at?: string;
    };
    cv_required?: boolean;
    candidate_email?: string;
    company_label?: string;
    [key: string]: unknown;
  };
};

// ---------------------------------------------------------------------------
// GET /api/public/token/{token}
// ---------------------------------------------------------------------------

export async function getPublicToken(token: string): Promise<TokenData> {
  const response = await axios.get(`/api/public/token/${token}`);
  const data = response.data;

  // Empty array = invalid token
  if (Array.isArray(data) && data.length === 0) {
    return { success: false, valid: false, expired: false };
  }

  if (data && data.expired_at) {
    const expired = new Date(data.expired_at).getTime() < Date.now();
    return {
      success: true,
      valid: !expired,
      expired,
      // Viktor returns cv_required at the ROOT of the token, as a sibling of params.
      // Merge it in so callers can read it off params like every other token field —
      // without this the CV-required invite accepts straight away instead of routing
      // to the upload page. Mirrors af/auth lib/viktor/token/get.ts.
      params: { ...data.params, cv_required: data.cv_required ?? false }
    };
  }

  return { success: false, valid: false, expired: false };
}

// ---------------------------------------------------------------------------
// POST /api/public/email/resend
// ---------------------------------------------------------------------------

export async function resendEmail(token: string): Promise<void> {
  await axios.post('/api/public/email/resend', { token });
}

// ---------------------------------------------------------------------------
// DELETE /api/public/user
// ---------------------------------------------------------------------------

export async function refuseInvitation(token: string): Promise<{ redirectUrl?: string }> {
  const response = await axios.delete('/api/public/user', { data: { token } });
  return response.data ?? {};
}

// ---------------------------------------------------------------------------
// PATCH /api/public/sharepool/{token}/{status}
// ---------------------------------------------------------------------------

export async function updateSharepoolStatus(
  token: string,
  status: 'accepted' | 'refused'
): Promise<{ status: number }> {
  const response = await axios.patch(`/api/public/sharepool/${token}/${status}`);
  return { status: response.status };
}

// ---------------------------------------------------------------------------
// POST /api/public/cv/{token}
// ---------------------------------------------------------------------------

export async function uploadCvByToken(token: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('cv', file, cvUploadFilename(file));
  await axios.post(`/api/public/cv/${token}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}

// ---------------------------------------------------------------------------
// POST /api/public/sourcing/{token}
// ---------------------------------------------------------------------------

export async function updateSourcingStatus(token: string, status: number): Promise<void> {
  await axios.post(`/api/public/sourcing/${token}`, { status });
}

// ---------------------------------------------------------------------------
// POST /api/public/sourceable/{token}
// ---------------------------------------------------------------------------

export async function updateSourceableStatus(token: string, sourceable: boolean): Promise<void> {
  await axios.post(`/api/public/sourceable/${token}`, { sourceable });
}

// ---------------------------------------------------------------------------
// POST /api/public/survey/{surveyUuid}/{token}
// ---------------------------------------------------------------------------

export async function postSurveyAnswer(
  surveyUuid: string,
  token: string,
  questionUuid: string,
  answer: string
): Promise<void> {
  await axios.post(`/api/public/survey/${surveyUuid}/${token}`, {
    question_uuid: questionUuid,
    answer
  });
}

// ---------------------------------------------------------------------------
// PATCH /api/public/email/confirm
// ---------------------------------------------------------------------------

export async function confirmEmail(token: string): Promise<{ status: number }> {
  const response = await axios.patch('/api/public/email/confirm', { token });
  return { status: response.status };
}

// ---------------------------------------------------------------------------
// GET /api/feature-flag/{name}
// ---------------------------------------------------------------------------

export async function getFeatureFlag(name: string): Promise<boolean> {
  try {
    const response = await axios.get(`/api/feature-flag/${name}`);
    return response.data?.is_active === true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// GET /api/public/safety?url=X
// ---------------------------------------------------------------------------

export async function validateUrl(url: string): Promise<{ valid: boolean }> {
  const response = await axios.get('/api/public/safety', { params: { url } });
  return response.data;
}
